import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    // Ensure we are updating the correct product
    const eventProductId = event.detail.data.productId;
    if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) {
      return;
    }

    // Attempt to find the new product-price element from the updated HTML
    // First try with block-id if available
    let newProductPrice = null;
    if (this.dataset.blockId) {
      newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    }
    
    // Fallback: just find any product-price if it's the only one or find by product-id
    if (!newProductPrice) {
      newProductPrice = event.detail.data.html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`) 
                       || event.detail.data.html.querySelector('product-price');
    }

    if (!newProductPrice) return;

    // Update price container using morph for stability
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    
    if (newPrice && currentPrice) {
      morph(currentPrice, newPrice);
    }

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      morph(currentNote, newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
